import { Field } from 'formik'
import styles from './CheckoutForm.module.css'
import { Fieldset } from '@local/do-secundo-fieldset'
import * as CustomerInfo from './CustomerInfo/CustomerInfo'
import { CheckoutDeliveryInfo } from './CheckoutDeliveryInfo/CheckoutDeliveryInfo'
import { DynamicFormSection } from '../DynamicForms/DynamicFormSection'
import { PromoCodeInput } from './PromoCode/PromoCodeInput'
import { TaxExempt } from './TaxExempt/TaxExempt'
import { getArgsForSubmit as getTipArgsForSubmit } from './Tip/Tip'
import cx from 'classnames'
import { CreditCard } from '../CreditCard/CreditCard'
import { LegalCopy } from '../LegalCopy/LegalCopy'
import { Button, ButtonType, ButtonVariant } from '@local/do-secundo-button'
import React, { useEffect } from 'react'
import { EventType } from '../../types/form'
import { Customer } from './CustomerInfo/CustomerInfo'
import { CheckoutFormValues } from './utils'
import { useRestaurant } from '@local/do-secundo-restaurant-provider/src'
import { Radio } from '@local/do-secundo-form'
import { getCheckTotal } from '../../utils/cart-helpers'
import { LoyaltyAccountLookup } from './LoyaltyAccountLookup'
import { CartCheckoutFields } from './CartCheckoutFields'
import { CreditCardFormSpi } from '../CreditCard/CreditCardFormSpi'
import { PayLaterOption } from '../../types/config'
import { Cart } from '../../types/cart'
import { CheckoutError } from '../CheckoutError/CheckoutError'
import { ConfirmOrderError } from '../CheckoutPage/utils'
import { useFlags } from '@local/do-secundo-feature-flags'
import { CheckboxField } from '@toasttab/buffet-pui-forms'
import { AdyenPayment } from '@toasttab/do-secundo-adyen-payment'

export const CheckoutFormBody = ({
  cart,
  eventType,
  handleSubmit,
  customer,
  values,
  setFieldValue,
  isSubmitting,
  canCheckout,
  confirmOrderError,
  setEditingAccount
}: {
  cart?: Cart
  eventType: EventType | undefined
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void
  customer: Customer
  values: CheckoutFormValues
  setFieldValue: (field: string, value: any) => void
  isSubmitting: boolean
  canCheckout: boolean
  confirmOrderError?: ConfirmOrderError
  setEditingAccount: (editing: boolean) => void
}) => {
  const { nvsCooSpi: spiEnabled } = useFlags()

  const {
    restaurantGuid,
    restaurantInfo,
    applicableConfigs: { payLaterOption, tdsConfig, marketingConfig },
    isIntlRx,
    currency,
    locale,
    country
  } = useRestaurant()

  const showSmsMarketingCheckbox = marketingConfig?.smsMarketingEnabled

  useEffect(() => {
    const tipAmount = getTipArgsForSubmit({ values }).tipAmount
    if (tipAmount > 0 && values.paymentType !== 'CREDIT') {
      setFieldValue('paymentType', 'CREDIT')
    } else if (tipAmount === 0 && getCheckTotal(cart) === 0) {
      setFieldValue('paymentType', 'ZERO_DOLLAR_ORDER')
    }
    // Only watch for when tip amount change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.paymentTip])

  if (!cart) {
    return null
  }

  const canShowTip = values.paymentType !== 'OTHER' && cart?.allowTipping
  const tipAmount = canShowTip ? getTipArgsForSubmit({ values }).tipAmount : 0

  const cartHasBalance = getCheckTotal(cart) > 0

  const showPayLaterAllowed =
    payLaterOption === PayLaterOption.ALLOW &&
    cartHasBalance &&
    !tdsConfig?.enabled
  const showPayLaterRequired =
    payLaterOption === PayLaterOption.REQUIRE &&
    cartHasBalance &&
    !tdsConfig?.enabled
  const showCreditCard =
    (cartHasBalance || tipAmount > 0) && !showPayLaterRequired

  const payLaterText = `${restaurantInfo?.name || 'The restaurant'}
    will follow up with payment options for this order.`

  return (
    <>
      <CustomerInfo.CustomerInfo
        cart={cart}
        customer={customer}
        companyName={values.companyName}
        setEditingAccount={setEditingAccount}
      />
      <CheckoutDeliveryInfo />
      {eventType && (
        <Fieldset label='Details'>
          <DynamicFormSection formFields={eventType.formFields} />
        </Fieldset>
      )}
      <LoyaltyAccountLookup cart={cart} customer={customer} />
      <Fieldset
        label='Payment Information'
        collapsable
        id='payment_type_fields'
      >
        {showPayLaterRequired && <h3>{payLaterText}</h3>}
        <PromoCodeInput cart={cart} />
        <TaxExempt cart={cart} />
        {showPayLaterAllowed && (
          <>
            <div className={styles['radio-tabs']}>
              <Field
                data-testid='payment-type-CC'
                style='tab'
                component={Radio}
                name='paymentType'
                id='payment_type_cc'
                label='Credit card'
                value='CREDIT'
              />
              <Field
                data-testid='payment-type-other'
                style='tab'
                component={Radio}
                name='paymentType'
                id='payment_type_other'
                label='Other'
                value='OTHER'
              />
            </div>
            {values.paymentType === 'OTHER' && (
              <div className='pt-2'>{payLaterText}</div>
            )}
          </>
        )}
        {showCreditCard && (
          <div
            className={cx({
              // Only show CC if paymentType is not OTHER aka Pay Later
              [styles.hidden]: values.paymentType === 'OTHER'
            })}
          >
            {isIntlRx ? (
              <AdyenPayment
                payment={{
                  checkAmount: getCheckTotal(cart),
                  tipAmount: tipAmount,
                  emailAddress: customer.email
                }}
                config={{
                  guid: restaurantGuid,
                  currency: currency!!,
                  locale: locale!!,
                  country: country,
                  channel: 'CATERING_OO'
                }}
                onPaymentCompleted={(paymentGuid) => {
                  setFieldValue('paymentGuid', paymentGuid)
                }}
              />
            ) : (
              <>
                {spiEnabled ? (
                  <Field
                    component={CreditCardFormSpi}
                    name='isPaymentInfoValid'
                    cartGuid={cart.guid}
                    amount={getCheckTotal(cart)}
                    tipAmount={tipAmount}
                    email={customer.email}
                    errorAuthorizingPayment={
                      confirmOrderError?.errorType === 'PAYMENT_AUTH'
                        ? confirmOrderError.error
                        : undefined
                    }
                  />
                ) : (
                  <Field
                    component={CreditCard}
                    name='encryptedCard'
                    amexAccepted={restaurantInfo?.amexAccepted}
                  />
                )}
              </>
            )}
          </div>
        )}
      </Fieldset>
      <CartCheckoutFields
        values={values}
        customer={customer}
        cart={cart}
        showTip={Boolean(canShowTip)}
      />
      <div className='px-6 pb-2'>
        {showSmsMarketingCheckbox && (
          <CheckboxField
            containerClassName='-mb-4'
            testId='sms-marketing-consent-checkbox'
            label='Text me recurring marketing messages including special offers from this restaurant or restaurant group.'
            name='marketingConsent.isSmsMarketingConsentGiven'
          />
        )}
        <CheckboxField
          testId='email-marketing-consent-checkbox'
          label='Email me news and offers from this restaurant or restaurant group.'
          name='marketingConsent.isEmailMarketingConsentGiven'
        />
      </div>
      {confirmOrderError && (
        <div className='px-5 pb-0'>
          <CheckoutError
            loading={isSubmitting}
            error={confirmOrderError.error}
            onSubmit={handleSubmit}
            cart={cart}
          />
        </div>
      )}
      <div className={styles.wrapper}>
        <Button
          data-testid='submit-button'
          id='submit-button'
          variant={ButtonVariant.PRIMARY}
          type={ButtonType.SUBMIT}
          loading={isSubmitting}
          disabled={!canCheckout}
        >
          Place order
        </Button>
        <LegalCopy />
        <div className='h-6' />
      </div>
    </>
  )
}
